import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { AlertTriangle, LogIn, Mail, RefreshCw } from 'lucide-react';

// Subtitle / post-login destination keyed off the `?redirect=` query param
// so the same shared login surface adapts to whichever product the user
// was bounced from. Default lane is Prioris (the headline platform).
// Keep keys aligned with the routes in App.js.
const REDIRECT_PRESETS = {
  '/engineering/prioris-app':       'Access Prioris — Materials Approval & Quality Control Platform',
  '/engineering/servicelife-nexus': 'Access Prioris — Materials Approval & Quality Control Platform',
  '/engineering/identity-testing':  'Access the Identity Testing Checker',
  '/engineering/carbonation-risk':  'Access the Carbonation Risk Tool',
};
const DEFAULT_SUBTITLE = REDIRECT_PRESETS['/engineering/prioris-app'];
const DEFAULT_REDIRECT = '/engineering/prioris-app';

export const Login = () => {
  const { login, resendVerification } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectParam = searchParams.get('redirect');
  const safeRedirect =
    redirectParam && redirectParam.startsWith('/') && !redirectParam.startsWith('//')
      ? redirectParam
      : DEFAULT_REDIRECT;
  const subtitle = REDIRECT_PRESETS[safeRedirect] || DEFAULT_SUBTITLE;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [unverified, setUnverified] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setUnverified(false);
    setResent(false);
    setLoading(true);
    try {
      await login(email, password);
      navigate(safeRedirect);
    } catch (err) {
      if (err.message.toLowerCase().includes('verify your email')) {
        setUnverified(true);
      }
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setResent(false);
    try {
      await resendVerification(email);
      setResent(true);
    } catch {
      // silently fail
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 pb-12 flex items-center justify-center px-4" style={{ background: '#0A0A0A' }}>
      <Card className="w-full max-w-md" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.1)' }}>
        <CardHeader className="text-center">
          <div className="text-xs tracking-[0.3em] text-emerald-500 font-semibold mb-2">DURABLE DESIGNS</div>
          <CardTitle className="text-2xl text-white" style={{ fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            Sign In
          </CardTitle>
          <p className="text-sm text-gray-400 mt-1">{subtitle}</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-start gap-2 text-red-400 text-sm bg-red-900/20 p-3 rounded" data-testid="login-error">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <span>{error}</span>
                  {unverified && (
                    <div className="mt-2">
                      <button
                        onClick={handleResend}
                        disabled={resending}
                        className="text-emerald-400 hover:underline inline-flex items-center gap-1 text-xs"
                        data-testid="login-resend-verification"
                      >
                        {resending ? <><RefreshCw className="w-3 h-3 animate-spin" /> Resending...</> : <><Mail className="w-3 h-3" /> Resend verification email</>}
                      </button>
                      {resent && <span className="text-emerald-400 text-xs ml-2" data-testid="login-resend-success">Sent!</span>}
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-300">Email</Label>
              <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} required
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500" placeholder="you@company.com" data-testid="login-email" />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-gray-300">Password</Label>
              <Link to="/auth/forgot-password" className="text-xs text-emerald-400 hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
            </div>
            <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} required
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500" placeholder="Min 6 characters" data-testid="login-password" />
            <Button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="login-submit">
              {loading ? 'Signing in...' : <><LogIn className="w-4 h-4 mr-2" /> Sign In</>}
            </Button>
            <p className="text-center text-sm text-gray-400">
              Don&apos;t have an account?{' '}
              <Link to="/auth/register" className="text-emerald-400 hover:underline" data-testid="register-link">Create free account</Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
